import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import { dealKeys } from './hooks';
import type { DealActivityRow } from '../profiles/timelineItems';

/**
 * The deal's own change log, for the Activity section.
 *
 * deal_activity has been written by a trigger on every edit since
 * 20270119000200 (stage, section, owner, amount, next action, outcome,
 * HubSpot link) and read by nothing. This is its first reader. Dev 1's
 * question in dev-notes, "does deal_activity have a client feed?", is
 * answered here: yes, now.
 */
const COLUMNS = 'id, event_type, details, actor_id, created_at';

export async function fetchDealActivity(
  client: SupabaseClient,
  dealId: string,
): Promise<DealActivityRow[]> {
  const { data, error } = await client
    .from('deal_activity')
    .select(COLUMNS)
    .eq('deal_id', dealId)
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

/** The same log across several deals: a lead's, or a company's. */
export async function fetchDealActivityFor(
  client: SupabaseClient,
  dealIds: readonly string[],
): Promise<DealActivityRow[]> {
  if (dealIds.length === 0) return [];
  const { data, error } = await client
    .from('deal_activity')
    .select(`${COLUMNS}, deal_id`)
    .in('deal_id', [...dealIds])
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as unknown as DealActivityRow[];
}

export function useDealActivityFor(dealIds: readonly string[]) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...dealKeys.all, 'activity', 'many', [...dealIds].sort().join(',')],
    queryFn: () => fetchDealActivityFor(client, dealIds),
    enabled: dealIds.length > 0,
    retry: false,
  });
}

export function useDealActivity(dealId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...dealKeys.all, 'activity', dealId],
    queryFn: () => fetchDealActivity(client, dealId),
    retry: false,
  });
}
